import { useMemo } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import {
  mapFlexibleNightsToDurationRange,
  convertDateRangesToMonthString,
} from 'lib/search/searchUtils'

interface UseExtractDatesAndOccupancyResult {
  extractedDates: App.ExtractDatesResult | undefined
  extractedOccupancy: App.ExtractOccupancyResult | undefined
}

export const useExtractDatesAndOccupancy = (
  typeaheadId: string | undefined,
): UseExtractDatesAndOccupancyResult => {
  const streamingData = useAppSelector(state =>
    typeaheadId ? state.search.typeaheadStreamingData[typeaheadId] : undefined,
  )

  const extractedDates = useMemo((): App.ExtractDatesResult | undefined => {
    const travelDates = streamingData?.travelDates?.data
    if (!travelDates) return undefined

    if (travelDates.type === 'specific') {
      return {
        checkinDate: travelDates.checkIn,
        checkoutDate: travelDates.checkOut,
        flexibleMonths: undefined,
        flexibleNights: undefined,
      }
    }

    if (travelDates.type === 'flexible') {
      return {
        checkinDate: undefined,
        checkoutDate: undefined,
        flexibleNights: mapFlexibleNightsToDurationRange(travelDates.numberOfNights),
        flexibleMonths: convertDateRangesToMonthString(travelDates.dateRanges),
      }
    }

    return undefined
  }, [streamingData?.travelDates?.data])

  const extractedOccupancy = useMemo((): App.ExtractOccupancyResult | undefined => {
    const occupancy = streamingData?.occupancy?.data
    if (!occupancy || occupancy.length === 0) return undefined

    return {
      occupancies: occupancy.map(room => ({
        adults: room.adults,
        children: room.childrenAges.length,
        childrenAge: room.childrenAges,
      })),
    }
  }, [streamingData?.occupancy?.data])

  return {
    extractedDates,
    extractedOccupancy,
  }
}
